import { useRef, useState } from "react";
import { Camera, ImagePlus, X, Loader2, AlertCircle, Lightbulb, UploadCloud } from "lucide-react";

export const MAX_PHOTOS = 8;

const Thumb = ({ photo, index, onRemove }) => (
  <div className="relative aspect-square overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200" data-testid={`photo-preview-${index}`}>
    <img src={photo.preview} alt={`Upload ${index + 1}`} className="h-full w-full object-cover" />
    {photo.status === "uploading" && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>}
    {photo.status === "error" && <div className="absolute inset-0 flex items-center justify-center bg-red-600/60"><AlertCircle className="h-6 w-6 text-white" /></div>}
    <button type="button" onClick={() => onRemove(photo.id)} data-testid={`photo-remove-${index}`} aria-label="Remove photo"
      className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/70 text-white backdrop-blur transition-transform active:scale-90">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const TIPS = ["Take wide shots and close-ups", "Include any damage, stains or problem spots", "Good light helps us estimate accurately"];

export const PhotoStep = ({ photos, onAdd, onRemove }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const remaining = MAX_PHOTOS - photos.length;
  const handle = (e) => { onAdd(Array.from(e.target.files || [])); e.target.value = ""; };
  const drop = (e) => { e.preventDefault(); setDragging(false); if (remaining > 0) onAdd(Array.from(e.dataTransfer.files || [])); };

  return (
    <div className="space-y-4" data-testid="photo-upload-dropzone">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid="photo-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} data-testid="photo-upload-input" />
      {remaining > 0 && (
        <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={drop}
          className={`rounded-2xl border-2 border-dashed p-5 text-center transition-colors ${dragging ? "border-[color:var(--brand)] brand-tint" : "border-slate-300 bg-white"}`}
          data-testid="photo-empty-dropzone">
          <UploadCloud className="mx-auto h-8 w-8 text-[color:var(--brand)]" strokeWidth={1.5} />
          <p className="mt-2 text-sm font-medium text-slate-700">Tap to add photos <span className="hidden sm:inline">or drag them here</span></p>
          <p className="text-xs text-slate-500">{remaining} of {MAX_PHOTOS} remaining</p>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => cameraRef.current.click()} data-testid="photo-camera-button"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] text-[15px] font-semibold text-white transition-transform active:scale-[0.98]">
              <Camera className="h-5 w-5" /> Take photo
            </button>
            <button type="button" onClick={() => galleryRef.current.click()} data-testid="photo-gallery-button"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-[15px] font-semibold text-slate-800 transition-transform active:scale-[0.98]">
              <ImagePlus className="h-5 w-5" /> Gallery
            </button>
          </div>
        </div>
      )}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="photo-preview-grid">
          {photos.map((p, i) => <Thumb key={p.id} photo={p} index={i} onRemove={onRemove} />)}
        </div>
      )}
      <div className="rounded-2xl bg-slate-100/80 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Lightbulb className="h-4 w-4 text-amber-500" />Photo tips</p>
        <ul className="mt-2 space-y-1">{TIPS.map((t) => <li key={t} className="text-sm text-slate-600">• {t}</li>)}</ul>
      </div>
      <p className="text-sm text-slate-500" data-testid="photo-count">{photos.length}/{MAX_PHOTOS} photos added</p>
    </div>
  );
};
